import { IonIcon, IonSpinner, useIonToast } from '@ionic/react';
import { notificationsOutline, trashOutline } from 'ionicons/icons';
import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router';
import ModalDialog from '../../../../../components/Modal/ModalDialog';
import ModalHeader from '../../../../../components/Modal/ModalHeader';
import { removerNotificacao } from '../../../../../Utils/Services';
import { MessageContext } from '../../../../../contexts/MessageContext';
import './Notificacoes.css';

type Props = {
  closeAction?: () => void;
  closeActionSimple?: () => void;
};

type Notificacao = {
  id: string;
  data: string;
  assunto: string;
  mensagem: string;
  tipo?: string;
  pkUrl?: string;
};

type Dialogo = { tipo: 'detalhes' | 'remover'; notificacao: Notificacao };

export default function Notificacoes({ closeAction, closeActionSimple }: Props) {
  const history = useHistory<any>();
  const { notificacoes } = useContext(MessageContext);
  const [removendo, setRemovendo] = useState(false);
  const [dialogo, setDialogo] = useState<Dialogo | null>(null);
  const [mostrarToast, fecharToast] = useIonToast();
  const lista: Notificacao[] = [...(notificacoes || [])].sort((a, b) => a.data < b.data ? 1 : a.data > b.data ? -1 : 0);
  const voltar = () => closeActionSimple ? closeActionSimple() : history.goBack();

  async function remover(id: string) {
    setRemovendo(true);
    try {
      const resposta = await fetch(removerNotificacao.url.replace('{id}', id.replace(/#/g, '%23')), {
        method: removerNotificacao.method,
        headers: [['token', localStorage.getItem('token') || '']],
        credentials: 'include'
      });
      if (resposta.status === 400 || resposta.status === 401) history.replace('/login/');
      if (resposta.status !== 200) throw new Error('Falha ao remover notificação');
      mostrarToast({ duration: 2000, position: 'middle', message: 'Removido com sucesso.' });
      return true;
    } catch {
      mostrarToast({
        position: 'middle',
        buttons: [{ text: 'OK', handler: () => fecharToast() }],
        message: 'Erro ao tentar remover.'
      });
      return false;
    } finally {
      setRemovendo(false);
    }
  }

  function abrirNotificacao(value: Notificacao) {
    const id = (value.pkUrl || '').replace(/#/g, '%23');
    switch (value.tipo) {
      case 'mensagem':
        history.push('./ordens/mensagem', { projetoid: id, projetonome: value.assunto });
        break;
      case 'vendasdetalhes':
        history.push('./vendas/vendasdetalhes', { vendaid: id, abrirInserirAgendamento: false });
        break;
      case 'ordensdetalhes':
        history.push('./ordens/ordensdetalhes', { projetoid: id, abrirInserirAgendamento: false });
        break;
      case 'ordensworkflow':
        history.push('./ordens/ordensworkflow', { projetoid: id, abrirInserirAgendamento: false });
        break;
      case 'agendamentosdetalhes':
        history.push('./agendamentos/agendamentosdetalhes', { agendamentoid: id });
        break;
      case 'acompanhar':
        history.push('./operacoes/acompanhar', { operacaoid: id });
        break;
      default:
        setDialogo({ tipo: 'detalhes', notificacao: value });
        return;
    }
    closeActionSimple?.();
  }

  async function confirmarDialogo() {
    if (!dialogo || removendo) return;
    if (dialogo.tipo === 'detalhes' || await remover(dialogo.notificacao.id)) setDialogo(null);
  }

  async function limparNotificacoes() {
    if (removendo) return;
    if (await remover('usuarios#' + (localStorage.getItem('cpf') || ''))) {
      if (closeAction) closeAction();
      else voltar();
    }
  }

  return (
    <div className="notifications-panel">
      <ModalHeader
        title="Notificações"
        subtitle="Acompanhe os avisos da sua loja."
        icon={notificationsOutline}
        layout={closeActionSimple ? 'responsive' : 'fullscreen'}
        onDismiss={voltar}
        disabled={removendo}
      />
      <div className="notifications-content" aria-busy={removendo}>
        {lista.length > 0 ? (
          <ul className="notifications-list" aria-label="Notificações recebidas">
            {lista.map(value => (
              <li key={value.id} className="notification-card">
                <button type="button" className="notification-details" disabled={removendo} onClick={() => abrirNotificacao(value)}>
                  <span className="notification-date">{new Date(value.data).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  <strong>{value.assunto}</strong>
                  <span className="notification-message">{value.mensagem}</span>
                </button>
                <button type="button" className="notification-remove" aria-label={`Remover notificação: ${value.assunto}`} disabled={removendo} onClick={() => setDialogo({ tipo: 'remover', notificacao: value })}>
                  <IonIcon icon={trashOutline} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="notifications-empty">
            <span><IonIcon icon={notificationsOutline} aria-hidden="true" /></span>
            <h3>Nenhuma notificação por aqui</h3>
            <p>Os novos avisos da sua loja aparecerão aqui.</p>
          </div>
        )}
      </div>
      <footer className="notifications-footer">
        <span>{lista.length === 1 ? '1 notificação' : `${lista.length} notificações`}</span>
        <button type="button" className="xeguei-modal-primary" disabled={removendo || lista.length === 0} onClick={limparNotificacoes}>
          {removendo ? <IonSpinner name="crescent" /> : <IonIcon icon={trashOutline} aria-hidden="true" />}
          {removendo ? 'Aguarde...' : 'Limpar notificações'}
        </button>
      </footer>
      <ModalDialog
        isOpen={dialogo !== null}
        title={dialogo?.tipo === 'remover' ? 'Remover notificação?' : 'Notificação recebida'}
        icon={dialogo?.tipo === 'remover' ? trashOutline : notificationsOutline}
        onDismiss={() => setDialogo(null)}
        onConfirm={confirmarDialogo}
        confirmLabel={dialogo?.tipo === 'remover' ? 'Remover' : 'Entendi'}
        cancelLabel={dialogo?.tipo === 'remover' ? 'Cancelar' : undefined}
        busy={removendo}
      >
        {dialogo?.tipo === 'remover' ? <p>Esta notificação será removida da sua lista.</p> : <>
          <h3 className="notification-dialog-subject">{dialogo?.notificacao.assunto}</h3>
          <p>{dialogo?.notificacao.mensagem}</p>
        </>}
      </ModalDialog>
    </div>
  );
}
